import { useEffect, useMemo, useState } from 'react'
import { Eye, FileText, Pencil, Plus, Signature } from 'lucide-react'
import { PageContainer, PageHeader } from '../../components/Layout'
import { Button, Card, CardContent, Input, Select, Badge, Modal } from '../../components/UI'
import { rentalTechService } from '../../services/api'
import { equipmentOptions, findEquipmentById, useRentalTechData } from './useRentalTechData'

const rateUnitOptions = [
  { value: 'hour', label: 'Hora' },
  { value: 'day', label: 'Dia' },
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mes' }
]

const customerTierOptions = [
  { value: 'standard', label: 'Standard' },
  { value: 'preferred', label: 'Preferente' },
  { value: 'enterprise', label: 'Enterprise' }
]

const defaultStartLocal = () => new Date(Date.now() + 3600000).toISOString().slice(0, 16)
const defaultEndLocal = () => new Date(Date.now() + 48 * 3600000).toISOString().slice(0, 16)

const createEmptyOrderForm = () => ({
  equipmentId: '',
  customerId: '',
  customerName: '',
  customerTier: 'standard',
  startUtc: defaultStartLocal(),
  endUtc: defaultEndLocal(),
  rateUnit: 'day',
  vatRatePct: 19
})

const toDateTimeLocal = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
}

const toOrderForm = (order) => ({
  equipmentId: order.equipment_id || '',
  customerId: order.customer_id || '',
  customerName: order.customer_name || '',
  customerTier: order.customer_tier || 'standard',
  startUtc: toDateTimeLocal(order.start_utc),
  endUtc: toDateTimeLocal(order.end_utc),
  rateUnit: order.rate_unit || 'day',
  vatRatePct: order.vat_rate_pct ?? 19
})

function OrderForm({
  form,
  setForm,
  equipments,
  customers,
  onSubmit,
  submitLabel,
  submitting,
  submitIcon,
  onCancel,
  title,
  subtitle
}) {
  const equipmentSelectOptions = useMemo(() => equipmentOptions(equipments), [equipments])
  const customerSelectOptions = useMemo(
    () => (customers || []).map((item) => ({ value: item.id, label: `${item.company_name} - ${item.contact_name || 'Sin contacto'}` })),
    [customers]
  )

  useEffect(() => {
    if (!form.equipmentId && equipmentSelectOptions.length > 0) {
      setForm((prev) => ({ ...prev, equipmentId: equipmentSelectOptions[0].value }))
    }
  }, [equipmentSelectOptions, form.equipmentId, setForm])

  const handleCustomerChange = (value) => {
    const customer = (customers || []).find((item) => item.id === value)
    setForm((prev) => ({
      ...prev,
      customerId: value || '',
      customerName: customer ? customer.company_name : prev.customerName,
      customerTier: customer ? customer.tier : prev.customerTier
    }))
  }

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-base font-semibold text-gray-900">{title}</h4>
        <p className="text-sm text-gray-500">{subtitle}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label="Equipo"
          options={equipmentSelectOptions}
          value={form.equipmentId}
          onChange={(e) => setForm((prev) => ({ ...prev, equipmentId: e.target.value }))}
          placeholder="Seleccionar equipo"
        />
        <Select
          label="Cliente registrado (opcional)"
          options={customerSelectOptions}
          value={form.customerId || ''}
          onChange={(e) => handleCustomerChange(e.target.value)}
          placeholder="Seleccionar cliente"
        />
        <Input
          label="Nombre del cliente / razon social"
          value={form.customerName}
          onChange={(e) => setForm((prev) => ({ ...prev, customerName: e.target.value }))}
          placeholder="Ej. Constructora Andina"
        />
        <Select
          label="Tipo de cliente (tier)"
          options={customerTierOptions}
          value={form.customerTier}
          onChange={(e) => setForm((prev) => ({ ...prev, customerTier: e.target.value }))}
          placeholder={null}
        />
        <Select
          label="Unidad de tarifa"
          options={rateUnitOptions}
          value={form.rateUnit}
          onChange={(e) => setForm((prev) => ({ ...prev, rateUnit: e.target.value }))}
          placeholder={null}
        />
        <Input
          label="IVA (%)"
          type="number"
          value={form.vatRatePct}
          onChange={(e) => setForm((prev) => ({ ...prev, vatRatePct: e.target.value }))}
          placeholder="19"
        />
        <Input
          label="Inicio del arrendamiento"
          type="datetime-local"
          value={form.startUtc}
          onChange={(e) => setForm((prev) => ({ ...prev, startUtc: e.target.value }))}
        />
        <Input
          label="Fin del arrendamiento"
          type="datetime-local"
          value={form.endUtc}
          onChange={(e) => setForm((prev) => ({ ...prev, endUtc: e.target.value }))}
        />
      </div>

      <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
        Campos gestionados: equipo, cliente, tier, unidad de tarifa, fechas inicio/fin e IVA. El sistema valida disponibilidad antes de generar o actualizar el contrato.
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t">
        <Button variant="secondary" onClick={onCancel}>Cancelar</Button>
        <Button onClick={onSubmit} loading={submitting} leftIcon={submitIcon}>{submitLabel}</Button>
      </div>
    </div>
  )
}

export default function RentalTechOrders() {
  const { equipments, orders, refresh } = useRentalTechData()
  const [customers, setCustomers] = useState([])
  const [search, setSearch] = useState('')
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedOrderId, setSelectedOrderId] = useState(null)

  const [createForm, setCreateForm] = useState(createEmptyOrderForm)
  const [editForm, setEditForm] = useState(createEmptyOrderForm)

  const [creating, setCreating] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [signingId, setSigningId] = useState(null)

  useEffect(() => {
    const loadCustomers = async () => {
      const response = await rentalTechService.getCustomers()
      setCustomers(response?.data?.data || [])
    }

    loadCustomers()
  }, [])

  const filteredOrders = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return orders
    return (orders || []).filter((order) =>
      [
        order.customer_name,
        order.quote_number,
        order.contract_number,
        order.customer_tier,
        order.rate_unit,
        String(order.total_amount || ''),
        String(order.id || '')
      ]
        .map((v) => String(v || '').toLowerCase())
        .some((v) => v.includes(q))
    )
  }, [orders, search])

  const selectedOrder = useMemo(
    () => (orders || []).find((item) => item.id === selectedOrderId) || null,
    [orders, selectedOrderId]
  )

  const getEquipmentLabel = (equipmentId) => {
    const equipment = findEquipmentById(equipments, equipmentId)
    return equipment ? `${equipment.code || 'EQ'} - ${equipment.name}` : equipmentId
  }

  const normalizeOrderPayload = (form) => ({
    equipmentId: form.equipmentId,
    customerId: form.customerId || null,
    customerName: form.customerName || 'Cliente sin nombre',
    customerTier: form.customerTier,
    startUtc: new Date(form.startUtc).toISOString(),
    endUtc: new Date(form.endUtc).toISOString(),
    rateUnit: form.rateUnit,
    vatRatePct: Number(form.vatRatePct)
  })

  const validateAndSaveNewOrder = async () => {
    setCreating(true)
    setMessage('')
    setError('')
    try {
      const payload = normalizeOrderPayload(createForm)

      const validation = await rentalTechService.validateAvailability({
        equipmentId: payload.equipmentId,
        startUtc: payload.startUtc,
        endUtc: payload.endUtc
      })
      if (!validation?.data?.data?.allowed) {
        setError(validation?.data?.data?.message || 'No disponible en el rango seleccionado.')
        return
      }

      const created = await rentalTechService.createOrder(payload)
      const createdOrder = created?.data?.data || null
      await refresh()
      setShowCreateModal(false)
      setCreateForm(createEmptyOrderForm())
      if (createdOrder?.id) setSelectedOrderId(createdOrder.id)
      setMessage(`Contrato ${createdOrder?.contract_number || ''} generado correctamente.`.trim())
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'No fue posible generar la reserva/cotizacion.')
    } finally {
      setCreating(false)
    }
  }

  const openViewModal = (order) => {
    setSelectedOrderId(order.id)
    setShowViewModal(true)
    setError('')
  }

  const openEditModal = (order) => {
    setSelectedOrderId(order.id)
    setEditForm(toOrderForm(order))
    setShowEditModal(true)
    setError('')
  }

  const updateOrder = async () => {
    if (!selectedOrderId) return

    setUpdating(true)
    setMessage('')
    setError('')
    try {
      const payload = normalizeOrderPayload(editForm)
      const validation = await rentalTechService.validateAvailability({
        equipmentId: payload.equipmentId,
        startUtc: payload.startUtc,
        endUtc: payload.endUtc,
        ignoreOrderId: selectedOrderId
      })
      if (!validation?.data?.data?.allowed) {
        setError(validation?.data?.data?.message || 'No disponible en el rango seleccionado.')
        return
      }

      const updated = await rentalTechService.updateOrder(selectedOrderId, payload)
      const updatedOrder = updated?.data?.data || null
      await refresh()
      setShowEditModal(false)
      setMessage(`Contrato ${updatedOrder?.contract_number || ''} actualizado.`.trim())
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'No fue posible actualizar el contrato.')
    } finally {
      setUpdating(false)
    }
  }

  const signOrder = async (orderId) => {
    setSigningId(orderId)
    setMessage('')
    setError('')
    try {
      await rentalTechService.signOrder(orderId, 'Firma Comercial Demo')
      await refresh()
      setMessage('Contrato firmado (mock) correctamente.')
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'No fue posible firmar el contrato.')
    } finally {
      setSigningId(null)
    }
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        <PageHeader
          title="Panel de Arrendamiento"
          subtitle="Consulta de contratos con acciones en popup para validar/generar, visualizar, editar y descargar PDF"
        />

        {message ? <div className="text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded px-3 py-2">{message}</div> : null}
        {error ? <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div> : null}

        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">Consulta de contratos</h3>
                <p className="text-xs text-gray-500">Listado de contratos de arrendamiento, cotizaciones y estado de firma.</p>
              </div>
              <div className="flex flex-col md:flex-row gap-2 w-full lg:w-auto">
                <div className="w-full md:w-96">
                  <Input placeholder="Buscar por cliente, contrato, cotizacion..." value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => { setCreateForm(createEmptyOrderForm()); setShowCreateModal(true) }}>
                  Validar y Generar
                </Button>
              </div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full min-w-[1200px] text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr className="text-left">
                    <th className="px-4 py-3">Contrato / Cotizacion</th>
                    <th className="px-4 py-3">Cliente</th>
                    <th className="px-4 py-3">Equipo</th>
                    <th className="px-4 py-3">Periodo</th>
                    <th className="px-4 py-3">Tarifa</th>
                    <th className="px-4 py-3">Total</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.map((order) => (
                    <tr key={order.id} className="border-b">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{order.contract_number || '-'}</div>
                        <div className="text-xs text-gray-500">{order.quote_number || '-'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div>{order.customer_name || '-'}</div>
                        <div className="text-xs text-gray-500">Tier: {order.customer_tier || '-'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-900">{getEquipmentLabel(order.equipment_id)}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        <div>{toDateTimeLocal(order.start_utc).replace('T', ' ') || '-'}</div>
                        <div>{toDateTimeLocal(order.end_utc).replace('T', ' ') || '-'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-900">{order.rate_unit || '-'}</div>
                        <div className="text-xs text-gray-500">${Number(order.unit_rate || 0).toLocaleString()}</div>
                      </td>
                      <td className="px-4 py-3 font-medium">${Number(order.total_amount || 0).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <Badge variant={order.signed ? 'success' : 'warning'}>{order.signed ? 'Firmado' : 'Pendiente firma'}</Badge>
                          <Badge variant="info">PDF</Badge>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          <Button size="sm" variant="secondary" leftIcon={<Eye className="w-3.5 h-3.5" />} onClick={() => openViewModal(order)}>
                            Ver
                          </Button>
                          <Button size="sm" variant="outline" leftIcon={<Pencil className="w-3.5 h-3.5" />} onClick={() => openEditModal(order)}>
                            Editar
                          </Button>
                          <a href={rentalTechService.getContractPdfUrl(order.id)} target="_blank" rel="noreferrer">
                            <Button size="sm" variant="ghost" leftIcon={<FileText className="w-3.5 h-3.5" />}>
                              PDF
                            </Button>
                          </a>
                          {!order.signed ? (
                            <Button size="sm" variant="primary" leftIcon={<Signature className="w-3.5 h-3.5" />} onClick={() => signOrder(order.id)} disabled={signingId === order.id}>
                              {signingId === order.id ? 'Firmando...' : 'Firmar'}
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!filteredOrders.length ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                        {orders.length ? 'No hay contratos que coincidan con la busqueda.' : 'No hay contratos generados aun.'}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          size="xl"
          title={
            <div className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary-600" />
              <span>Nueva Reserva / Cotizacion</span>
            </div>
          }
        >
          <OrderForm
            form={createForm}
            setForm={setCreateForm}
            equipments={equipments}
            customers={customers}
            onSubmit={validateAndSaveNewOrder}
            submitLabel="Validar y generar contrato"
            submitting={creating}
            submitIcon={<Plus className="w-4 h-4" />}
            onCancel={() => setShowCreateModal(false)}
            title="Formulario Nueva Reserva / Cotizacion"
            subtitle="Completa los campos del contrato. El sistema valida disponibilidad y genera cotizacion + contrato + PDF."
          />
        </Modal>

        <Modal
          isOpen={showViewModal}
          onClose={() => setShowViewModal(false)}
          size="lg"
          title={
            <div className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-primary-600" />
              <span>Visualizar contrato</span>
            </div>
          }
        >
          {selectedOrder ? (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs text-gray-500">Contrato</p>
                  <p className="font-medium">{selectedOrder.contract_number}</p>
                  <p className="text-xs text-gray-500 mt-1">Cotizacion: {selectedOrder.quote_number}</p>
                </div>
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs text-gray-500">Cliente</p>
                  <p className="font-medium">{selectedOrder.customer_name}</p>
                  <p className="text-xs text-gray-500 mt-1">Tier: {selectedOrder.customer_tier}</p>
                </div>
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs text-gray-500">Equipo</p>
                  <p className="font-medium">{getEquipmentLabel(selectedOrder.equipment_id)}</p>
                  <p className="text-xs text-gray-500 mt-1">Unidad: {selectedOrder.rate_unit}</p>
                </div>
                <div className="rounded-lg border border-gray-200 p-3">
                  <p className="text-xs text-gray-500">Periodo</p>
                  <p>{toDateTimeLocal(selectedOrder.start_utc).replace('T', ' ')}</p>
                  <p className="text-xs text-gray-500 mt-1">hasta {toDateTimeLocal(selectedOrder.end_utc).replace('T', ' ')}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-gray-500">Tarifa</p>
                  <p className="font-medium">${Number(selectedOrder.unit_rate || 0).toLocaleString()}</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-gray-500">Subtotal</p>
                  <p className="font-medium">${Number(selectedOrder.subtotal || 0).toLocaleString()}</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-gray-500">IVA</p>
                  <p className="font-medium">${Number(selectedOrder.vat_amount || 0).toLocaleString()}</p>
                </div>
                <div className="rounded-lg border p-3 bg-gray-50">
                  <p className="text-xs text-gray-500">Total</p>
                  <p className="font-semibold">${Number(selectedOrder.total_amount || 0).toLocaleString()}</p>
                </div>
              </div>

              <div className="flex flex-wrap justify-end gap-2 pt-2 border-t">
                <a href={rentalTechService.getContractPdfUrl(selectedOrder.id)} target="_blank" rel="noreferrer">
                  <Button variant="ghost" leftIcon={<FileText className="w-4 h-4" />}>Descargar PDF</Button>
                </a>
                {!selectedOrder.signed ? (
                  <Button variant="secondary" leftIcon={<Signature className="w-4 h-4" />} onClick={() => signOrder(selectedOrder.id)} disabled={signingId === selectedOrder.id}>
                    {signingId === selectedOrder.id ? 'Firmando...' : 'Firmar (mock)'}
                  </Button>
                ) : null}
                <Button
                  variant="outline"
                  leftIcon={<Pencil className="w-4 h-4" />}
                  onClick={() => {
                    setShowViewModal(false)
                    openEditModal(selectedOrder)
                  }}
                >
                  Editar
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500">No hay contrato seleccionado.</div>
          )}
        </Modal>

        <Modal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          size="xl"
          title={
            <div className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary-600" />
              <span>Editar contrato</span>
            </div>
          }
        >
          <OrderForm
            form={editForm}
            setForm={setEditForm}
            equipments={equipments}
            customers={customers}
            onSubmit={updateOrder}
            submitLabel="Guardar cambios"
            submitting={updating}
            submitIcon={<Pencil className="w-4 h-4" />}
            onCancel={() => setShowEditModal(false)}
            title="Formulario de edicion de contrato"
            subtitle="Actualiza los campos de la reserva/cotizacion y recalcula valores del contrato."
          />
        </Modal>
      </div>
    </PageContainer>
  )
}
